
import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { getEmployeePayslipStatement, getEmployees, clearError, clearSuccess } from '../../../../redux/Slices/authSlice';
import { FaUser, FaSearch, FaPrint, FaEye, FaFileExcel } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import * as XLSX from 'xlsx';
import { useTitle } from '../../../../shared/hooks/TitleContext';
import PdfPreviewModal from '../../Accounting/Reports/components/PdfPreviewModal';
import { convertArabicNumbersToEnglish } from '../../../../shared/utils.js/utils';

const PayslipsStatement = () => {
  const [searchParams] = useSearchParams();
  const dispatch = useDispatch();
  const { setTitle } = useTitle();
  const { employeePayslipStatement, employees, employeesPagination, isLoading, error, success } = useSelector((state) => state.auth);
  const [statementEmployeeId, setStatementEmployeeId] = useState(searchParams.get('employeeId') || '');
  const [statementYear, setStatementYear] = useState(searchParams.get('year') ? parseInt(searchParams.get('year')) : new Date().getFullYear());
  const [statementMonth, setStatementMonth] = useState(searchParams.get('month') ? parseInt(searchParams.get('month')) : new Date().getMonth() + 1);
  const [showStatement, setShowStatement] = useState(false);
  const [employeeDropdownOpen, setEmployeeDropdownOpen] = useState(false);
  const [employeeSearchTerm, setEmployeeSearchTerm] = useState('');
  const [employeeCurrentPage, setEmployeeCurrentPage] = useState(1);
  const employeeSearchInputRef = React.useRef(null);
  const employeeSearchTimeoutRef = React.useRef(null);
  const employeeDropdownRef = React.useRef(null);
  const pdfContainerRef = React.useRef(null);
  const lastErrorRef = useRef({ message: null, time: 0 });
  const lastSuccessRef = useRef({ message: null, time: 0 });
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  const [pdfType, setPdfType] = useState(null);
  const [pdfPayload, setPdfPayload] = useState(null);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [previewPayslip, setPreviewPayslip] = useState(null);
  const { t, i18n } = useTranslation('global');
  const isRTL = i18n.language === 'ar';
  const textAlign = isRTL ? 'right' : 'left';
  const dir = isRTL ? 'rtl' : 'ltr';
  const publicUrl = process.env.PUBLIC_URL || '';
  const headerLogo = `${publicUrl}/ACE.png`;

  useEffect(() => {
    setTitle('payslips.statement.title');
  }, [setTitle]);

  // Load employees for dropdown 
  useEffect(() => {
    if (employeeDropdownOpen) {
      dispatch(getEmployees({ page: employeeCurrentPage, per_page: 10, search: employeeSearchTerm || undefined, language: i18n.language }));
    }
  }, [dispatch, employeeCurrentPage, employeeDropdownOpen, i18n.language]);

  // Search Employees
  useEffect(() => {
    if (employeeSearchTerm !== undefined && employeeDropdownOpen) {
      if (employeeSearchTimeoutRef.current) clearTimeout(employeeSearchTimeoutRef.current);
      employeeSearchTimeoutRef.current = setTimeout(() => {
        setEmployeeCurrentPage(1);
        dispatch(getEmployees({ page: 1, per_page: 10, search: employeeSearchTerm || undefined, language: i18n.language }));
      }, 300);
    }
    return () => {
      if (employeeSearchTimeoutRef.current) clearTimeout(employeeSearchTimeoutRef.current);
    };
  }, [employeeSearchTerm, employeeDropdownOpen, dispatch, i18n.language]);

  useEffect(() => {
    if (employeeDropdownOpen && employeeSearchInputRef.current) {
      employeeSearchInputRef.current.focus();
    }
  }, [employeeDropdownOpen]);

  // Close Dropdown when click outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (employeeDropdownRef.current && !employeeDropdownRef.current.contains(event.target)) {
        setEmployeeDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Auto-load statement if employeeId is in URL
  useEffect(() => {
    if (statementEmployeeId && searchParams.get('employeeId')) {
      handleGetEmployeeStatement();
    }
  }, []);

  useEffect(() => {
    dispatch(clearSuccess());
    dispatch(clearError());
  }, [dispatch]);

  useEffect(() => {
    if (success) {
      const now = Date.now();
      const last = lastSuccessRef.current;
      if (!last.message || last.message !== success || now - last.time > 2000) {
        toast.success(success, {
          rtl: isRTL,
          position: "top-right",
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true
        });
        lastSuccessRef.current = { message: success, time: now };
      }
      dispatch(clearSuccess());
    }
  }, [success, dispatch, isRTL]);

  useEffect(() => {
    if (error) {
      const now = Date.now();
      const last = lastErrorRef.current;
      if (!last.message || last.message !== error || now - last.time > 2000) {
        toast.error(error, {
          rtl: isRTL,
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true
        });
        lastErrorRef.current = { message: error, time: now };
      }
      setTimeout(() => dispatch(clearError()), 3000);
    }
  }, [error, dispatch, isRTL]);

  const formatCurrency = (amount) => {
    return parseFloat(amount).toLocaleString('en-US') + ' ' + t('payslips.currency');
  };

  const generatePdf = async (type, payload, filename) => {
    try {
      setPdfType(type);
      setPdfPayload(payload);
      await new Promise((resolve) => requestAnimationFrame(resolve));
      if (document.fonts && document.fonts.ready) {
        await document.fonts.ready;
      }
      await new Promise((resolve) => setTimeout(resolve, 300));
      setIsGeneratingPdf(true);

      const element = pdfContainerRef.current;
      if (!element) {
        toast.error(t('payslips.print.error', { defaultValue: isRTL ? 'قالب الطباعة غير متوفر' : 'Print template not available' }), { rtl: isRTL });
        return;
      }

      const devicePixelRatio = window.devicePixelRatio || 1;
      const scale = Math.max(3, devicePixelRatio * 2);

      const canvas = await html2canvas(element, {
        scale,
        useCORS: true,
        logging: false,
        backgroundColor: '#ffffff',
        letterRendering: true,
        allowTaint: false,
        removeContainer: false,
        imageTimeout: 15000,
        onclone: (clonedDoc) => {
          const clonedElement = clonedDoc.getElementById('payslips-pdf');
          if (clonedElement) {
            clonedElement.style.fontFamily = '"Times New Roman Bold", "Times New Roman", "Times", serif';
            clonedElement.style.fontWeight = 'bold';
            clonedElement.style.direction = isRTL ? 'rtl' : 'ltr';
            clonedElement.style.setProperty('-webkit-font-smoothing', 'antialiased');
            clonedElement.style.setProperty('-moz-osx-font-smoothing', 'grayscale');
            clonedElement.style.textRendering = 'optimizeLegibility';
            const allElements = clonedElement.querySelectorAll('*');
            allElements.forEach((el) => {
              if (el.style) {
                el.style.fontFamily = '"Times New Roman Bold", "Times New Roman", "Times", serif';
                el.style.fontWeight = 'bold';
                el.style.setProperty('-webkit-font-smoothing', 'antialiased');
                el.style.setProperty('-moz-osx-font-smoothing', 'grayscale');
                el.style.textRendering = 'optimizeLegibility';
              }
            });
          }
        }
      });

      const imgData = canvas.toDataURL('image/png', 1.0);
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, pdfHeight, undefined, 'FAST');
      pdf.save(filename);
    } catch (error) {
      console.error('Payslips PDF error:', error);
      toast.error(t('payslips.print.error', { defaultValue: isRTL ? 'حدث خطأ أثناء إنشاء ملف كشف الرواتب' : 'An error occurred while creating the payslips file' }), { rtl: isRTL });
    } finally {
      setIsGeneratingPdf(false);
      setPdfType(null);
      setPdfPayload(null);
    }
  };

  const handlePrintStatementPdf = async (payslip) => {
    if (!payslip) {
      toast.error('لا توجد بيانات لهذا الكشف');
      return;
    }
    const identifier = payslip.employee?.employee_code || payslip.employee?.name || payslip.id;
    await generatePdf('statement', payslip, `ACE_Payslip_${identifier}_${payslip.pay_period || ''}.pdf`);
  };

  const handleGetEmployeeStatement = async () => {
    if (!statementEmployeeId) {
      toast.error(t('payslips.statement.selectEmployee', { defaultValue: 'يرجى اختيار الموظف' }), { rtl: isRTL });
      return;
    }
    await dispatch(getEmployeePayslipStatement({ employeeId: statementEmployeeId, year: statementYear, month: statementMonth }));
    setShowStatement(true);
  };

  // Handle preview single payslip
  const handlePreviewSingle = (payslip) => {
    if (!payslip) {
      toast.error(t('payslips.print.noData', { defaultValue: isRTL ? 'لا توجد بيانات لهذا الكشف' : 'No data available for this payslip' }), { rtl: isRTL });
      return;
    }
    setPreviewPayslip(payslip);
    setPdfType('statement');
    setPdfPayload(payslip);
    setIsPreviewOpen(true);
  };

  // Handle preview all payslips
  const handlePreviewAll = () => {
    if (!employeePayslipStatement || !Array.isArray(employeePayslipStatement) || employeePayslipStatement.length === 0) {
      toast.error(t('payslips.statement.noData', { defaultValue: 'لا توجد بيانات للمعاينة' }), { rtl: isRTL });
      return;
    }
    setPreviewPayslip({ all: true });
    setPdfType('all');
    setPdfPayload(null);
    setIsPreviewOpen(true);
  };

  // Handle print all (for PdfPreviewModal)
  const handlePrintAll = async () => {
    if (!employeePayslipStatement || !Array.isArray(employeePayslipStatement) || employeePayslipStatement.length === 0) {
      toast.error(t('payslips.statement.noData', { defaultValue: 'لا توجد بيانات للطباعة' }), { rtl: isRTL });
      return;
    }
    // Print first payslip as example, or implement multi-page PDF
    if (employeePayslipStatement.length > 0) {
      await handlePrintStatementPdf(employeePayslipStatement[0]);
    }
  };

  // Export single payslip to Excel
  const handleExportPayslipToExcel = (payslip) => {
    if (!payslip) {
      toast.error(t('payslips.export.noData', { defaultValue: isRTL ? 'لا توجد بيانات للتصدير' : 'No data to export' }), { rtl: isRTL });
      return;
    }

    try {
      const data = [{
        [t('payslips.statement.employeeName', { defaultValue: isRTL ? 'اسم الموظف' : 'Employee Name' })]: (isRTL ? payslip.employee?.name_ar : payslip.employee?.name_en) || payslip.employee?.name || '—',
        [t('payslips.statement.employeeCode', { defaultValue: 'رقم الموظف' })]: payslip.employee?.employee_code || '—',
        [t('payslips.statement.period', { defaultValue: 'الفترة' })]: payslip.pay_period || '—',
        [t('payslips.statement.grossSalary', { defaultValue: 'الراتب الإجمالي' })]: payslip.gross_salary || 0,
        [t('payslips.statement.netSalary', { defaultValue: 'الراتب الصافي' })]: payslip.net_salary || 0,
        [t('payslips.statement.status', { defaultValue: 'الحالة' })]: payslip.status || '—',
      }];

      if (payslip.breakdown) {
        if (payslip.breakdown.base_salary) {
          data[0][t('payslips.statement.baseSalary', { defaultValue: 'الراتب الأساسي' })] = payslip.breakdown.base_salary;
        }
        if (payslip.breakdown.allowances) {
          Object.entries(payslip.breakdown.allowances).forEach(([key, value]) => {
            data[0][`${t('payslips.statement.allowance', { defaultValue: 'بدل' })} ${key}`] = value;
          });
        }
        if (payslip.breakdown.fixed_deductions) {
          payslip.breakdown.fixed_deductions.forEach((deduction, idx) => {
            data[0][`${t('payslips.statement.deduction', { defaultValue: 'خصم' })} ${idx + 1}`] = deduction.amount || deduction;
          });
        }
      }

      const ws = XLSX.utils.json_to_sheet(data);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, isRTL ? 'كشف راتب' : 'Payslip');
      const identifier = payslip.employee?.employee_code || payslip.employee?.name || payslip.id;
      const fileName = `${isRTL ? 'كشف_راتب' : 'Payslip'}_${identifier}_${payslip.pay_period || ''}_${new Date().toISOString().split('T')[0]}.xlsx`;
      XLSX.writeFile(wb, fileName);
      toast.success(t('payslips.export.success', { defaultValue: 'تم تصدير البيانات بنجاح' }), { rtl: isRTL });
    } catch (error) {
      console.error('Excel export error:', error);
      toast.error(t('payslips.export.error', { defaultValue: 'حدث خطأ أثناء التصدير' }), { rtl: isRTL });
    }
  };

  // Export all payslips to Excel
  const handleExportAllToExcel = () => {
    if (!employeePayslipStatement || !Array.isArray(employeePayslipStatement) || employeePayslipStatement.length === 0) {
      toast.error(t('payslips.statement.noData', { defaultValue: 'لا توجد بيانات للتصدير' }), { rtl: isRTL });
      return;
    }

    try {
      const data = employeePayslipStatement.map((payslip) => {
        const row = {
          [t('payslips.statement.employeeName', { defaultValue: isRTL ? 'اسم الموظف' : 'Employee Name' })]: (isRTL ? payslip.employee?.name_ar : payslip.employee?.name_en) || payslip.employee?.name || '—',
          [t('payslips.statement.employeeCode', { defaultValue: 'رقم الموظف' })]: payslip.employee?.employee_code || '—',
          [t('payslips.statement.period', { defaultValue: 'الفترة' })]: payslip.pay_period || '—',
          [t('payslips.statement.grossSalary', { defaultValue: 'الراتب الإجمالي' })]: payslip.gross_salary || 0,
          [t('payslips.statement.netSalary', { defaultValue: 'الراتب الصافي' })]: payslip.net_salary || 0,
          [t('payslips.statement.status', { defaultValue: 'الحالة' })]: payslip.status || '—',
        };

        if (payslip.breakdown) {
          if (payslip.breakdown.base_salary) {
            row[t('payslips.statement.baseSalary', { defaultValue: 'الراتب الأساسي' })] = payslip.breakdown.base_salary;
          }
          if (payslip.breakdown.allowances) {
            Object.entries(payslip.breakdown.allowances).forEach(([key, value]) => {
              row[`${t('payslips.statement.allowance', { defaultValue: 'بدل' })} ${key}`] = value;
            });
          }
        }

        return row;
      });

      const ws = XLSX.utils.json_to_sheet(data);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, isRTL ? 'كشوف الرواتب' : 'Payslips');
      const fileName = `${isRTL ? 'كشوف_الرواتب' : 'Payslips'}_${new Date().toISOString().split('T')[0]}.xlsx`;
      XLSX.writeFile(wb, fileName);
      toast.success(t('payslips.export.success', { defaultValue: 'تم تصدير البيانات بنجاح' }), { rtl: isRTL });
    } catch (error) {
      console.error('Excel export error:', error);
      toast.error(t('payslips.export.error', { defaultValue: 'حدث خطأ أثناء التصدير' }), { rtl: isRTL });
    }
  };

  const statementPrintData = pdfType === 'statement' ? pdfPayload : null;
  const formatPrintCurrency = (value) => {
    const formatted = Number(value || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    return convertArabicNumbersToEnglish(formatted) + ' ' + t('payslips.currency');
  };

  return (
    <div style={{
      padding: '30px',
      backgroundColor: '#14263b',
      minHeight: 'calc(100vh - 80px)',
      color: 'white',
      direction: dir
    }}>
      <div style={{ marginBottom: '30px' }}>
        <h1 style={{
          fontSize: '24px',
          fontWeight: 'bold',
          color: 'white',
          marginBottom: '20px',
          textAlign
        }}>
          {t('payslips.statement.title', { defaultValue: 'كشف تفصيلي للرواتب' })}
        </h1>

        {/* كشف تفصيلي لموظف */}
        <div style={{
          backgroundColor: '#1b3350',
          borderRadius: '12px',
          padding: '20px',
          marginBottom: '20px',
          border: '1px solid #333'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '15px', direction: dir }}>
            <FaUser style={{ color: '#AC2000', fontSize: '20px', order: isRTL ? 2 : 0 }} />
            <h2 style={{ margin: 0, fontSize: '18px', color: 'white', textAlign, direction: dir }}>
              {t('payslips.statement.title', { defaultValue: isRTL ? 'كشف تفصيلي لموظف' : 'Employee Payslip Statement' })}
            </h2>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '15px', alignItems: 'flex-end' }}>
            <div style={{ position: 'relative' }} ref={employeeDropdownRef}>
              <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px', color: '#ccc', textAlign, direction: dir }}>
                {t('payslips.statement.employee', { defaultValue: isRTL ? 'الموظف' : 'Employee' })}
              </label>
              <div
                onClick={() => {
                  setEmployeeDropdownOpen((p) => !p);
                  if (employeeDropdownOpen) {
                    setEmployeeSearchTerm('');
                    setEmployeeCurrentPage(1);
                  }
                }}
                style={{
                  width: '100%',
                  padding: '10px',
                  backgroundColor: '#1a1f2e',
                  border: '1px solid #333',
                  borderRadius: '8px',
                  color: statementEmployeeId ? 'white' : '#666',
                  fontSize: '14px',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  cursor: 'pointer',
                  boxSizing: 'border-box',
                  minHeight: '40px',
                  direction: dir,
                  textAlign
                }}
              >
                <span style={{ direction: dir, textAlign }}>
                  {statementEmployeeId && employees?.find(emp => emp.id.toString() === statementEmployeeId.toString())
                    ? (isRTL ? employees.find(emp => emp.id.toString() === statementEmployeeId.toString()).name_ar : employees.find(emp => emp.id.toString() === statementEmployeeId.toString()).name_en) || employees.find(emp => emp.id.toString() === statementEmployeeId.toString()).name
                    : t('payslips.statement.selectEmployee', { defaultValue: isRTL ? 'اختر الموظف' : 'Select Employee' })}
                </span>
                <span style={{ fontSize: '12px', order: isRTL ? -1 : 1 }}>{employeeDropdownOpen ? '▲' : '▼'}</span>
              </div>
              {employeeDropdownOpen && (
                <div style={{
                  position: 'absolute',
                  top: '100%',
                  left: 0,
                  right: 0,
                  backgroundColor: '#1b3350',
                  border: '1px solid #333',
                  borderRadius: '8px',
                  marginTop: '5px',
                  maxHeight: '300px',
                  overflowY: 'auto',
                  zIndex: 1000,
                  boxShadow: '0 4px 6px rgba(0,0,0,0.3)'
                }}>
                  <div style={{ padding: '10px', borderBottom: '1px solid #333', position: 'sticky', top: 0, backgroundColor: '#1b3350' }}>
                    <div style={{ position: 'relative', marginBottom: '10px' }}>
                      <FaSearch style={{
                        position: 'absolute',
                        [isRTL ? 'right' : 'left']: '10px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        color: '#666',
                        fontSize: '14px',
                        pointerEvents: 'none'
                      }} />
                      <input
                        ref={employeeSearchInputRef}
                        type="text"
                        value={employeeSearchTerm}
                        onChange={(e) => setEmployeeSearchTerm(e.target.value)}
                        placeholder={t('payslips.statement.searchEmployee', { defaultValue: isRTL ? 'ابحث عن موظف...' : 'Search for employee...' })}
                        style={{
                          width: '100%',
                          padding: isRTL ? '8px 35px 8px 8px' : '8px 8px 8px 35px',
                          backgroundColor: '#1a1f2e',
                          border: '1px solid #333',
                          borderRadius: '6px',
                          color: 'white',
                          fontSize: '14px',
                          boxSizing: 'border-box'
                        }}
                      />
                    </div>
                    {employeesPagination && (employeesPagination.last_page > 1 || (employeesPagination.last_page === 1 && employeesPagination.current_page === 1)) && (
                      <div style={{ padding: '8px', borderTop: '1px solid #333', background: '#14263b' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                          <span style={{ color: '#ccc', fontSize: 11, direction: dir, textAlign }}>{t('common.page', { defaultValue: isRTL ? 'الصفحة' : 'Page' })} {employeesPagination.current_page} {t('common.of', { defaultValue: isRTL ? 'من' : 'of' })} {employeesPagination.last_page}</span>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 4, flexWrap: 'wrap' }}>
                          <button type="button" onClick={(e) => { e.stopPropagation(); if (employeesPagination.current_page > 1) setEmployeeCurrentPage(1); }} disabled={employeesPagination.current_page === 1} style={{ padding: '4px 8px', backgroundColor: employeesPagination.current_page === 1 ? '#2a2a2a' : '#1a1f2e', color: employeesPagination.current_page === 1 ? '#666' : 'white', border: '1px solid #444', borderRadius: '4px', cursor: employeesPagination.current_page === 1 ? 'not-allowed' : 'pointer', fontSize: 10, fontWeight: 'bold', minWidth: '28px' }}>««</button>
                          <button type="button" onClick={(e) => { e.stopPropagation(); if (employeesPagination.current_page > 1) setEmployeeCurrentPage(employeesPagination.current_page - 1); }} disabled={employeesPagination.current_page === 1} style={{ padding: '4px 8px', backgroundColor: employeesPagination.current_page === 1 ? '#2a2a2a' : '#1a1f2e', color: employeesPagination.current_page === 1 ? '#666' : 'white', border: '1px solid #444', borderRadius: '4px', cursor: employeesPagination.current_page === 1 ? 'not-allowed' : 'pointer', fontSize: 10, fontWeight: 'bold', minWidth: '28px' }}>‹</button>
                          {(() => {
                            const pages = [];
                            const current = employeesPagination.current_page;
                            const last = employeesPagination.last_page;
                            const showPages = 5;
                            let start = Math.max(1, current - Math.floor(showPages / 2));
                            let end = Math.min(last, start + showPages - 1);
                            if (end - start < showPages - 1) start = Math.max(1, end - showPages + 1);
                            for (let i = start; i <= end; i++) pages.push(i);
                            return pages.map((page) => (
                              <button key={page} type="button" onClick={(e) => { e.stopPropagation(); setEmployeeCurrentPage(page); }} style={{ padding: '4px 8px', backgroundColor: page === current ? '#AC2000' : '#1a1f2e', color: 'white', border: page === current ? '1px solid #AC2000' : '1px solid #444', borderRadius: '4px', cursor: 'pointer', fontSize: 10, fontWeight: page === current ? 'bold' : 'normal', minWidth: '28px' }}>{page}</button>
                            ));
                          })()}
                          <button type="button" onClick={(e) => { e.stopPropagation(); if (employeesPagination.current_page < employeesPagination.last_page) setEmployeeCurrentPage(employeesPagination.current_page + 1); }} disabled={employeesPagination.current_page === employeesPagination.last_page} style={{ padding: '4px 8px', backgroundColor: employeesPagination.current_page === employeesPagination.last_page ? '#2a2a2a' : '#1a1f2e', color: employeesPagination.current_page === employeesPagination.last_page ? '#666' : 'white', border: '1px solid #444', borderRadius: '4px', cursor: employeesPagination.current_page === employeesPagination.last_page ? 'not-allowed' : 'pointer', fontSize: 10, fontWeight: 'bold', minWidth: '28px' }}>›</button>
                          <button type="button" onClick={(e) => { e.stopPropagation(); if (employeesPagination.current_page < employeesPagination.last_page) setEmployeeCurrentPage(employeesPagination.last_page); }} disabled={employeesPagination.current_page === employeesPagination.last_page} style={{ padding: '4px 8px', backgroundColor: employeesPagination.current_page === employeesPagination.last_page ? '#2a2a2a' : '#1a1f2e', color: employeesPagination.current_page === employeesPagination.last_page ? '#666' : 'white', border: '1px solid #444', borderRadius: '4px', cursor: employeesPagination.current_page === employeesPagination.last_page ? 'not-allowed' : 'pointer', fontSize: 10, fontWeight: 'bold', minWidth: '28px' }}>»»</button>
                        </div>
                      </div>
                    )}
                  </div>
                  <div>
                    {employees && employees.length > 0 ? (
                      <>
                        {employees.map((employee) => (
                          <div
                            key={employee.id}
                            onClick={() => {
                              setStatementEmployeeId(employee.id.toString());
                              setEmployeeDropdownOpen(false);
                              setEmployeeSearchTerm('');
                              setEmployeeCurrentPage(1);
                              setShowStatement(false);
                            }}
                            style={{
                              padding: '12px',
                              cursor: 'pointer',
                              color: 'white',
                              borderBottom: '1px solid #333',
                              fontSize: '14px',
                              backgroundColor: statementEmployeeId === employee.id.toString() ? '#AC2000' : 'transparent'
                            }}
                            onMouseEnter={(e) => {
                              if (statementEmployeeId !== employee.id.toString()) {
                                e.target.style.backgroundColor = '#1a1f2e';
                              }
                            }}
                            onMouseLeave={(e) => {
                              if (statementEmployeeId !== employee.id.toString()) {
                                e.target.style.backgroundColor = 'transparent';
                              }
                            }}
                          >
                            <div style={{ fontWeight: 'bold', direction: dir, textAlign }}>{(isRTL ? employee.name_ar : employee.name_en) || employee.name}</div>
                            <div style={{ fontSize: '12px', color: '#ccc', direction: dir, textAlign }}>{employee.employee_code}</div>
                          </div>
                        ))}
                      </>
                    ) : (
                      <div style={{ padding: '20px', textAlign: 'center', color: '#666', fontSize: '14px' }}>
                        {t('payslips.statement.noEmployees', { defaultValue: 'لا توجد موظفين' })}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px', color: '#ccc', textAlign, direction: dir }}>
                {t('payslips.statement.year', { defaultValue: isRTL ? 'السنة' : 'Year' })}
              </label>
              <input
                type="number"
                value={statementYear}
                onChange={(e) => { setStatementYear(parseInt(e.target.value) || new Date().getFullYear()); setShowStatement(false); }}
                style={{
                  padding: '10px',
                  backgroundColor: '#1a1f2e',
                  border: '1px solid #333',
                  borderRadius: '8px',
                  color: 'white',
                  fontSize: '14px',
                  width: '100%',
                  boxSizing: 'border-box',
                  direction: dir,
                  textAlign
                }}
              />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px', color: '#ccc', textAlign, direction: dir }}>
                {t('payslips.statement.month', { defaultValue: isRTL ? 'الشهر' : 'Month' })}
              </label>
              <input
                type="number"
                min="1"
                max="12"
                value={statementMonth}
                onChange={(e) => { setStatementMonth(parseInt(e.target.value) || 1); setShowStatement(false); }}
                style={{
                  padding: '10px',
                  backgroundColor: '#1a1f2e',
                  border: '1px solid #333',
                  borderRadius: '8px',
                  color: 'white',
                  fontSize: '14px',
                  width: '100%',
                  boxSizing: 'border-box',
                  direction: dir,
                  textAlign
                }}
              />
            </div>
          </div>
          <div style={{ display: 'flex', justifyContent: 'center', marginTop: '15px' }}>
            <button
              onClick={handleGetEmployeeStatement}
              disabled={isLoading}
              style={{
                backgroundColor: '#17a2b8',
                color: 'white',
                border: 'none',
                padding: '10px 20px',
                borderRadius: '8px',
                cursor: isLoading ? 'not-allowed' : 'pointer',
                fontSize: '14px',
                fontWeight: 'bold',
                opacity: isLoading ? 0.6 : 1
              }}
            >
              {t('payslips.statement.show', { defaultValue: isRTL ? 'عرض الكشف' : 'Show Statement' })}
            </button>
          </div>
          {showStatement && employeePayslipStatement && Array.isArray(employeePayslipStatement) && employeePayslipStatement.length > 0 && (
            <div style={{ marginTop: '20px', maxHeight: '600px', overflowY: 'auto' }}>
              {employeePayslipStatement.map((payslip) => (
                <div key={payslip.id} style={{ marginBottom: '30px', backgroundColor: '#1a1f2e', borderRadius: '8px', padding: '20px', border: '1px solid #333' }}>
                  <div style={{ display: 'flex', justifyContent: isRTL ? 'flex-end' : 'flex-start', gap: '10px', marginBottom: '15px', flexWrap: 'nowrap', direction: dir }}>
                    <button
                      onClick={() => handlePreviewSingle(payslip)}
                      style={{
                        backgroundColor: '#1f5ff1',
                        color: 'white',
                        border: 'none',
                        padding: '8px 16px',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        fontSize: '14px',
                        fontWeight: 'bold'
                      }}
                    >
                      <FaEye /> {t('payslips.preview.single', { defaultValue: 'عرض' })}
                    </button>
                    <button
                      onClick={() => handlePrintStatementPdf(payslip)}
                      disabled={isGeneratingPdf}
                      style={{
                        backgroundColor: '#17a2b8',
                        color: 'white',
                        border: 'none',
                        padding: '8px 16px',
                        borderRadius: '8px',
                        cursor: isGeneratingPdf ? 'not-allowed' : 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        fontSize: '14px',
                        fontWeight: 'bold',
                        opacity: isGeneratingPdf ? 0.7 : 1
                      }}
                    >
                      <FaPrint /> {isGeneratingPdf ? t('payslips.loading', { defaultValue: isRTL ? 'جاري التحميل...' : 'Loading...' }) : t('payslips.actions.print', { defaultValue: isRTL ? 'طباعة' : 'Print' })}
                    </button>
                    <button
                      onClick={() => handleExportPayslipToExcel(payslip)}
                      style={{
                        backgroundColor: '#28a745',
                        color: 'white',
                        border: 'none',
                        padding: '8px 16px',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        fontSize: '14px',
                        fontWeight: 'bold'
                      }}
                    >
                      <FaFileExcel /> {t('payslips.export.single', { defaultValue: 'تصدير Excel' })}
                    </button>
                  </div>
                  {/* معلومات الموظف */}
                  <div style={{ marginBottom: '20px', paddingBottom: '15px', borderBottom: '2px solid #333' }}>
                    <h3 style={{ color: 'white', marginBottom: '15px', fontSize: '18px', textAlign }}>
                      {t('payslips.statement.employeeInfo', { defaultValue: isRTL ? 'معلومات الموظف' : 'Employee Information' })}
                    </h3>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '15px', direction: dir }}>
                      <div style={{ direction: dir, textAlign }}>
                        <span style={{ color: '#ccc', fontSize: '14px' }}>{t('payslips.statement.employeeName', { defaultValue: isRTL ? 'الاسم' : 'Name' })}: </span>
                        <span style={{ color: 'white', fontSize: '14px', fontWeight: 'bold' }}>{(isRTL ? payslip.employee?.name_ar : payslip.employee?.name_en) || payslip.employee?.name || '-'}</span>
                      </div>
                      <div style={{ direction: dir, textAlign }}>
                        <span style={{ color: '#ccc', fontSize: '14px' }}>{t('payslips.statement.employeeCode', { defaultValue: 'رقم الموظف' })}: </span>
                        <span style={{ color: 'white', fontSize: '14px' }}>{payslip.employee?.employee_code || '-'}</span>
                      </div>
                      <div style={{ direction: dir, textAlign }}>
                        <span style={{ color: '#ccc', fontSize: '14px' }}>{t('payslips.statement.email', { defaultValue: 'البريد الإلكتروني' })}: </span>
                        <span style={{ color: 'white', fontSize: '14px' }}>{payslip.employee?.email || '-'}</span>
                      </div>
                      <div style={{ direction: dir, textAlign }}>
                        <span style={{ color: '#ccc', fontSize: '14px' }}>{t('payslips.statement.phone', { defaultValue: 'الهاتف' })}: </span>
                        <span style={{ color: 'white', fontSize: '14px' }}>{payslip.employee?.phone || '-'}</span>
                      </div>
                    </div>
                  </div>

                  {/* معلومات الكشف */}
                  <div style={{ marginBottom: '20px', paddingBottom: '15px', borderBottom: '2px solid #333' }}>
                    <h3 style={{ color: 'white', marginBottom: '15px', fontSize: '18px', textAlign }}>
                      {t('payslips.statement.payslipInfo', { defaultValue: isRTL ? 'معلومات الكشف' : 'Payslip Information' })}
                    </h3>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '15px', direction: dir }}>
                      <div style={{ direction: dir, textAlign }}>
                        <span style={{ color: '#ccc', fontSize: '14px' }}>{t('payslips.statement.period', { defaultValue: 'الفترة' })}: </span>
                        <span style={{ color: 'white', fontSize: '14px', fontWeight: 'bold' }}>{payslip.pay_period || '-'}</span>
                      </div>
                      <div style={{ direction: dir, textAlign }}>
                        <span style={{ color: '#ccc', fontSize: '14px' }}>{t('payslips.statement.grossSalary', { defaultValue: 'الراتب الإجمالي' })}: </span>
                        <span style={{ color: 'white', fontSize: '14px', fontWeight: 'bold' }}>{payslip.gross_salary ? formatCurrency(payslip.gross_salary) : '-'}</span>
                      </div>
                      <div style={{ direction: dir, textAlign }}>
                        <span style={{ color: '#ccc', fontSize: '14px' }}>{t('payslips.statement.netSalary', { defaultValue: 'الراتب الصافي' })}: </span>
                        <span style={{ color: '#17a2b8', fontSize: '14px', fontWeight: 'bold' }}>{payslip.net_salary ? formatCurrency(payslip.net_salary) : '-'}</span>
                      </div>
                      <div style={{ direction: dir, textAlign }}>
                        <span style={{ color: '#ccc', fontSize: '14px' }}>{t('payslips.statement.status', { defaultValue: 'الحالة' })}: </span>
                        <span style={{
                          padding: '5px 15px',
                          borderRadius: '20px',
                          backgroundColor: payslip.status === 'generated' ? 'rgba(172, 32, 0, 0.4)' : payslip.status === 'posted' ? 'rgba(0, 123, 255, 0.4)' : 'rgba(220, 53, 69, 0.4)',
                          color: 'white',
                          fontSize: '12px',
                          fontWeight: 'bold'
                        }}>
                          {payslip.status === 'generated' ? t('payslips.status.generated') : payslip.status === 'posted' ? t('payslips.status.posted') : payslip.status || '-'}
                        </span>
                      </div>
                      {payslip.generated_at && (
                        <div style={{ direction: dir, textAlign }}>
                          <span style={{ color: '#ccc', fontSize: '14px' }}>{t('payslips.statement.generatedAt', { defaultValue: 'تاريخ الإنشاء' })}: </span>
                          <span style={{ color: 'white', fontSize: '14px' }}>{new Date(payslip.generated_at).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Breakdown */}
                  {payslip.breakdown && (
                    <div style={{ marginBottom: '20px' }}>
                      <h3 style={{ color: 'white', marginBottom: '15px', fontSize: '18px', textAlign }}>
                        {t('payslips.statement.breakdown', { defaultValue: isRTL ? 'تفاصيل الراتب' : 'Salary Breakdown' })}
                      </h3>
                      <div style={{ overflowX: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                          <thead>
                            <tr style={{ backgroundColor: '#1b3350' }}>
                              <th style={{ padding: '12px', textAlign: 'center', color: 'white', borderBottom: '2px solid #333', fontSize: '14px' }}>
                                {t('payslips.statement.item', { defaultValue: 'البند' })}
                              </th>
                              <th style={{ padding: '12px', textAlign: 'center', color: 'white', borderBottom: '2px solid #333', fontSize: '14px' }}>
                                {t('payslips.statement.amount', { defaultValue: 'المبلغ' })}
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {payslip.breakdown.base_salary && (
                              <tr>
                                <td style={{ padding: '12px', textAlign: 'center', color: 'white', fontSize: '14px', borderBottom: '1px solid #333' }}>
                                  {t('payslips.statement.baseSalary', { defaultValue: 'الراتب الأساسي' })}
                                </td>
                                <td style={{ padding: '12px', textAlign: 'center', color: 'white', fontSize: '14px', fontWeight: 'bold', borderBottom: '1px solid #333' }}>
                                  {formatCurrency(payslip.breakdown.base_salary)}
                                </td>
                              </tr>
                            )}
                            {payslip.breakdown.allowances && Object.keys(payslip.breakdown.allowances).length > 0 && (
                              <>
                                {Object.entries(payslip.breakdown.allowances).map(([key, value]) => (
                                  <tr key={key}>
                                    <td style={{ padding: '12px', textAlign: 'center', color: 'white', fontSize: '14px', borderBottom: '1px solid #333' }}>
                                      {t(`payslips.statement.allowance.${key}`, { defaultValue: key })}
                                    </td>
                                    <td style={{ padding: '12px', textAlign: 'center', color: '#17a2b8', fontSize: '14px', fontWeight: 'bold', borderBottom: '1px solid #333' }}>
                                      +{formatCurrency(value)}
                                    </td>
                                  </tr>
                                ))}
                              </>
                            )}
                            {payslip.breakdown.fixed_deductions && payslip.breakdown.fixed_deductions.length > 0 && (
                              <>
                                {payslip.breakdown.fixed_deductions.map((deduction, index) => (
                                  <tr key={index}>
                                    <td style={{ padding: '12px', textAlign: 'center', color: 'white', fontSize: '14px', borderBottom: '1px solid #333' }}>
                                      {deduction.name || t('payslips.statement.deduction', { defaultValue: 'خصم' })}
                                    </td>
                                    <td style={{ padding: '12px', textAlign: 'center', color: '#dc3545', fontSize: '14px', fontWeight: 'bold', borderBottom: '1px solid #333' }}>
                                      -{formatCurrency(deduction.amount || deduction)}
                                    </td>
                                  </tr>
                                ))}
                              </>
                            )}
                            {payslip.breakdown.calculation_details && payslip.breakdown.calculation_details.absence_deduction && (
                              <tr>
                                <td style={{ padding: '12px', textAlign: 'center', color: 'white', fontSize: '14px', borderBottom: '1px solid #333' }}>
                                  {t('payslips.statement.absenceDeduction', { defaultValue: 'خصم الغياب' })}
                                </td>
                                <td style={{ padding: '12px', textAlign: 'center', color: '#dc3545', fontSize: '14px', fontWeight: 'bold', borderBottom: '1px solid #333' }}>
                                  -{formatCurrency(payslip.breakdown.calculation_details.absence_deduction)}
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  {/* تفاصيل الحساب */}
                  {payslip.breakdown?.calculation_details && (
                    <div>
                      <h3 style={{ color: 'white', marginBottom: '15px', fontSize: '18px', textAlign }}>
                        {t('payslips.statement.calculationDetails', { defaultValue: isRTL ? 'تفاصيل الحساب' : 'Calculation Details' })}
                      </h3>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '15px' }}>
                        {payslip.breakdown.calculation_details.total_work_days !== undefined && (
                          <div>
                            <span style={{ color: '#ccc', fontSize: '14px' }}>{t('payslips.statement.totalWorkDays', { defaultValue: 'إجمالي أيام العمل' })}: </span>
                            <span style={{ color: 'white', fontSize: '14px', fontWeight: 'bold' }}>{payslip.breakdown.calculation_details.total_work_days}</span>
                          </div>
                        )}
                        {payslip.breakdown.calculation_details.attended_days !== undefined && (
                          <div>
                            <span style={{ color: '#ccc', fontSize: '14px' }}>{t('payslips.statement.attendedDays', { defaultValue: 'أيام الحضور' })}: </span>
                            <span style={{ color: '#17a2b8', fontSize: '14px', fontWeight: 'bold' }}>{payslip.breakdown.calculation_details.attended_days}</span>
                          </div>
                        )}
                        {payslip.breakdown.calculation_details.leave_days !== undefined && (
                          <div>
                            <span style={{ color: '#ccc', fontSize: '14px' }}>{t('payslips.statement.leaveDays', { defaultValue: 'أيام الإجازة' })}: </span>
                            <span style={{ color: '#17a2b8', fontSize: '14px', fontWeight: 'bold' }}>{payslip.breakdown.calculation_details.leave_days}</span>
                          </div>
                        )}
                        {payslip.breakdown.calculation_details.absent_days !== undefined && (
                          <div>
                            <span style={{ color: '#ccc', fontSize: '14px' }}>{t('payslips.statement.absentDays', { defaultValue: 'أيام الغياب' })}: </span>
                            <span style={{ color: '#dc3545', fontSize: '14px', fontWeight: 'bold' }}>{payslip.breakdown.calculation_details.absent_days}</span>
                          </div>
                        )}
                        {payslip.breakdown.calculation_details.daily_rate !== undefined && (
                          <div>
                            <span style={{ color: '#ccc', fontSize: '14px' }}>{t('payslips.statement.dailyRate', { defaultValue: 'المعدل اليومي' })}: </span>
                            <span style={{ color: 'white', fontSize: '14px', fontWeight: 'bold' }}>{formatCurrency(payslip.breakdown.calculation_details.daily_rate)}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
          {showStatement && employeePayslipStatement && (!Array.isArray(employeePayslipStatement) || employeePayslipStatement.length === 0) && (
            <div style={{ marginTop: '20px', padding: '20px', textAlign: 'center', color: '#666', fontSize: '14px' }}>
              {t('payslips.statement.noData', { defaultValue: 'لا توجد بيانات' })}
            </div>
          )}
        </div>
      </div>

      {pdfType && (
        <div style={{ position: 'absolute', top: '-99999px', left: '-99999px' }}>
          <div
            id="payslips-pdf"
            ref={pdfContainerRef}
            style={{
              width: '794px',
              minHeight: '1123px',
              backgroundColor: '#ffffff',
              color: '#000000',
              fontFamily: '"Times New Roman Bold", "Times New Roman", "Times", serif',
              fontWeight: 'bold',
              padding: '40px',
              boxSizing: 'border-box',
              direction: isRTL ? 'rtl' : 'ltr'
            }}
          >
            {/* Outer thick border container */}
            <div style={{
              border: '5px solid #000',
              padding: '3px',
              backgroundColor: '#ffffff',
              boxSizing: 'border-box',
              width: '100%',
              minHeight: '1043px',
              height: 'auto',
              overflow: 'visible'
            }}>
              {/* Inner border container */}
              <div style={{
                border: '2px solid #000',
                minHeight: '1033px',
                height: '1033px',
                padding: '18px 18px 10px',
                position: 'relative',
                display: 'flex',
                flexDirection: 'column',
                fontFamily: '"Times New Roman Bold", "Times New Roman", "Times", serif',
                fontWeight: 'bold',
                WebkitFontSmoothing: 'antialiased',
                MozOsxFontSmoothing: 'grayscale',
                textRendering: 'optimizeLegibility',
                direction: isRTL ? 'rtl' : 'ltr',
                backgroundColor: '#ffffff',
                boxSizing: 'border-box',
                width: '100%',
                overflow: 'visible'
              }}>
                {/* Background Logo - Fixed in center vertically */}
                <div style={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  width: '100%',
                  height: '600px',
                  backgroundImage: `url(${headerLogo})`,
                  backgroundSize: '95%',
                  backgroundPosition: 'center center',
                  backgroundRepeat: 'no-repeat',
                  opacity: 0.1,
                  zIndex: 0,
                  pointerEvents: 'none'
                }} />
                <div style={{ position: 'relative', zIndex: 1, display: 'flex', flexDirection: 'column', height: '100%', justifyContent: 'space-between' }}>
                  <div style={{ flex: '0 0 auto' }}>
                    {/* Header */}
                    <img
                      src={`${publicUrl}/HeaderAce.png`}
                      alt="Header"
                      style={{
                        width: '100%',
                        height: 'auto',
                        objectFit: 'contain',
                        marginBottom: '8px',
                        borderBottom: '4px double #000',
                        paddingBottom: '8px',
                        flexShrink: 0
                      }}
                      crossOrigin="anonymous"
                    />

                    <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                      {statementPrintData && (
                        <>
                          <div style={{ marginTop: '12px', textAlign: 'center', marginBottom: '20px' }}>
                            <h2 style={{ fontSize: '20px', fontWeight: '700', color: '#000080', margin: 0, direction: dir }}>{t('payslips.print.labels.employeePayslip', { defaultValue: isRTL ? 'كشف راتب موظف' : 'Employee Payslip' })}</h2>
                            <div style={{ fontSize: '14px', marginTop: '4px', direction: dir }}>{statementPrintData.pay_period || ''}</div>
                          </div>
                          <div style={{ border: '1px solid #000', padding: '12px', marginBottom: '12px' }}>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '10px', fontSize: '13px', lineHeight: 1.6, direction: dir, textAlign }}>
                              <div style={{ direction: dir, textAlign }}><strong>{t('payslips.print.labels.name', { defaultValue: isRTL ? 'الاسم' : 'Name' })}:</strong> {(isRTL ? statementPrintData.employee?.name_ar : statementPrintData.employee?.name_en) || statementPrintData.employee?.name || '—'}</div>
                              <div style={{ direction: dir, textAlign }}><strong>{t('payslips.print.labels.code', { defaultValue: isRTL ? 'الكود' : 'Code' })}:</strong> {statementPrintData.employee?.employee_code || '—'}</div>
                              <div style={{ direction: dir, textAlign }}><strong>{t('payslips.print.labels.email', { defaultValue: isRTL ? 'البريد' : 'Email' })}:</strong> {statementPrintData.employee?.email || '—'}</div>
                              <div style={{ direction: dir, textAlign }}><strong>{t('payslips.print.labels.phone', { defaultValue: isRTL ? 'الهاتف' : 'Phone' })}:</strong> {statementPrintData.employee?.phone || '—'}</div>
                            </div>
                          </div>
                          <div style={{ border: '1px solid #000', padding: '12px', marginBottom: '12px' }}>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '10px', fontSize: '13px', lineHeight: 1.6, direction: dir, textAlign }}>
                              <div style={{ direction: dir, textAlign }}><strong>{t('payslips.print.labels.payPeriod', { defaultValue: isRTL ? 'الفترة' : 'Pay Period' })}:</strong> {statementPrintData.pay_period || '—'}</div>
                              <div style={{ direction: dir, textAlign }}><strong>{t('payslips.print.labels.grossSalary', { defaultValue: isRTL ? 'الراتب الإجمالي' : 'Gross Salary' })}:</strong> {formatPrintCurrency(statementPrintData.gross_salary)}</div>
                              <div style={{ direction: dir, textAlign }}><strong>{t('payslips.print.labels.netSalary', { defaultValue: isRTL ? 'الراتب الصافي' : 'Net Salary' })}:</strong> {formatPrintCurrency(statementPrintData.net_salary)}</div>
                              <div style={{ direction: dir, textAlign }}><strong>{t('payslips.print.labels.status', { defaultValue: isRTL ? 'الحالة' : 'Status' })}:</strong> {statementPrintData.status === 'generated' ? t('payslips.status.generated', { defaultValue: isRTL ? 'تم التوليد' : 'Generated' }) : statementPrintData.status === 'posted' ? t('payslips.status.posted', { defaultValue: isRTL ? 'مرحل' : 'Posted' }) : statementPrintData.status || '—'}</div>
                            </div>
                          </div>

                          {statementPrintData.breakdown && (
                            <div style={{ marginBottom: '12px' }}>
                              <div style={{ fontWeight: '700', fontSize: '15px', color: '#0d2a50', marginBottom: '8px', direction: dir, textAlign }}>{t('payslips.print.labels.salaryDetails', { defaultValue: isRTL ? 'تفاصيل الراتب' : 'Salary Details' })}</div>
                              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px', direction: dir }}>
                                <thead>
                                  <tr style={{ backgroundColor: '#e3f2fd', color: '#0d2a50' }}>
                                    <th style={{ border: '1px solid #000', padding: '8px', textAlign }}>{t('payslips.print.labels.item', { defaultValue: isRTL ? 'البند' : 'Item' })}</th>
                                    <th style={{ border: '1px solid #000', padding: '8px', textAlign }}>{t('payslips.print.labels.amount', { defaultValue: isRTL ? 'المبلغ' : 'Amount' })}</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {statementPrintData.breakdown.base_salary && (
                                    <tr>
                                      <td style={{ border: '1px solid #000', padding: '8px', textAlign }}>{t('payslips.print.labels.baseSalary', { defaultValue: isRTL ? 'الراتب الأساسي' : 'Base Salary' })}</td>
                                      <td style={{ border: '1px solid #000', padding: '8px', fontWeight: '700', textAlign }}>{formatPrintCurrency(statementPrintData.breakdown.base_salary)}</td>
                                    </tr>
                                  )}
                                  {statementPrintData.breakdown.allowances && Object.keys(statementPrintData.breakdown.allowances).map((key) => (
                                    <tr key={key}>
                                      <td style={{ border: '1px solid #000', padding: '8px', textAlign }}>{t('payslips.print.labels.allowance', { defaultValue: isRTL ? 'بدل' : 'Allowance' })} {key}</td>
                                      <td style={{ border: '1px solid #000', padding: '8px', color: '#1976d2', fontWeight: '700', textAlign }}>+{formatPrintCurrency(statementPrintData.breakdown.allowances[key])}</td>
                                    </tr>
                                  ))}
                                  {statementPrintData.breakdown.fixed_deductions && Array.isArray(statementPrintData.breakdown.fixed_deductions) && statementPrintData.breakdown.fixed_deductions.length > 0 && statementPrintData.breakdown.fixed_deductions.map((deduction, idx) => (
                                    <tr key={`deduction-${idx}`}>
                                      <td style={{ border: '1px solid #000', padding: '8px', textAlign }}>{deduction.name || t('payslips.print.labels.deduction', { defaultValue: isRTL ? 'خصم' : 'Deduction' })}</td>
                                      <td style={{ border: '1px solid #000', padding: '8px', color: '#c0392b', fontWeight: '700', textAlign }}>-{formatPrintCurrency(deduction.amount || deduction)}</td>
                                    </tr>
                                  ))}
                                  {statementPrintData.breakdown.calculation_details?.absence_deduction && (
                                    <tr>
                                      <td style={{ border: '1px solid #000', padding: '8px', textAlign }}>{t('payslips.print.labels.absenceDeduction', { defaultValue: isRTL ? 'خصم الغياب' : 'Absence Deduction' })}</td>
                                      <td style={{ border: '1px solid #000', padding: '8px', color: '#c0392b', fontWeight: '700', textAlign }}>-{formatPrintCurrency(statementPrintData.breakdown.calculation_details.absence_deduction)}</td>
                                    </tr>
                                  )}
                                </tbody>
                              </table>
                            </div>
                          )}

                          {statementPrintData.breakdown?.calculation_details && (
                            <div style={{ border: '1px solid #000', padding: '12px', fontSize: '12px', lineHeight: 1.7, direction: dir, textAlign }}>
                              <div style={{ fontWeight: '700', marginBottom: '8px', color: '#0d2a50', direction: dir, textAlign }}>{t('payslips.print.labels.calculationDetails', { defaultValue: isRTL ? 'تفاصيل الحساب' : 'Calculation Details' })}</div>
                              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '10px', direction: dir }}>
                                {statementPrintData.breakdown.calculation_details.total_work_days !== undefined && (
                                  <div style={{ direction: dir, textAlign }}><strong>{t('payslips.print.labels.totalWorkDays', { defaultValue: isRTL ? 'أيام العمل' : 'Total Work Days' })}:</strong> {statementPrintData.breakdown.calculation_details.total_work_days}</div>
                                )}
                                {statementPrintData.breakdown.calculation_details.attended_days !== undefined && (
                                  <div style={{ direction: dir, textAlign }}><strong>{t('payslips.print.labels.attendedDays', { defaultValue: isRTL ? 'أيام الحضور' : 'Attended Days' })}:</strong> {statementPrintData.breakdown.calculation_details.attended_days}</div>
                                )}
                                {statementPrintData.breakdown.calculation_details.leave_days !== undefined && (
                                  <div style={{ direction: dir, textAlign }}><strong>{t('payslips.print.labels.leaveDays', { defaultValue: isRTL ? 'أيام الإجازة' : 'Leave Days' })}:</strong> {statementPrintData.breakdown.calculation_details.leave_days}</div>
                                )}
                                {statementPrintData.breakdown.calculation_details.absent_days !== undefined && (
                                  <div style={{ direction: dir, textAlign }}><strong>{t('payslips.print.labels.absentDays', { defaultValue: isRTL ? 'أيام الغياب' : 'Absent Days' })}:</strong> {statementPrintData.breakdown.calculation_details.absent_days}</div>
                                )}
                                {statementPrintData.breakdown.calculation_details.daily_rate !== undefined && (
                                  <div style={{ direction: dir, textAlign }}><strong>{t('payslips.print.labels.dailyRate', { defaultValue: isRTL ? 'المعدل اليومي' : 'Daily Rate' })}:</strong> {formatPrintCurrency(statementPrintData.breakdown.calculation_details.daily_rate)}</div>
                                )}
                              </div>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  </div>

                  {/* Footer */}
                  <div style={{ flexShrink: 0, paddingTop: '20px', marginTop: 'auto' }}>
                    <img
                      src={`${publicUrl}/FooterAce.png`}
                      alt="Footer"
                      style={{
                        width: '100%',
                        height: 'auto',
                        objectFit: 'contain',
              borderTop: "4px double #000",
              paddingTop: "8px",
}}
                      crossOrigin="anonymous"
                    />
                    <div style={{ textAlign: 'center', marginTop: '6px', fontFamily: '"Times New Roman", "Times", serif' }}>
                      <div style={{
                        fontStyle: 'italic',
                        marginTop: '2px',
                        display: 'inline-flex',
                        alignItems: 'baseline',
                        gap: '6px',
                        fontSize: '12px',
                        fontFamily: '"Times New Roman Bold", "Times New Roman", "Times", serif',
                        fontWeight: 'bold'
                      }}>
                        <span style={{ fontFamily: '"Times New Roman", "Times", serif' }}>Page</span>
                        <strong style={{ fontSize: '13px', fontFamily: '"Times New Roman", "Times", serif' }}>1 / 1</strong>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Preview Modal */}
      <PdfPreviewModal
        isOpen={isPreviewOpen && (!!previewPayslip || (pdfType === 'statement' && pdfPayload))}
        title={previewPayslip?.all ? t('payslips.statement.title', { defaultValue: isRTL ? 'كشف تفصيلي للرواتب' : 'Detailed Payslip Statement' }) : t('payslips.print.labels.employeePayslip', { defaultValue: isRTL ? 'كشف راتب موظف' : 'Employee Payslip' })}
        onClose={() => {
          setIsPreviewOpen(false);
          setPreviewPayslip(null);
          setPdfType(null);
          setPdfPayload(null);
        }}
        onPrint={previewPayslip?.all ? handlePrintAll : () => (previewPayslip || pdfPayload) && handlePrintStatementPdf(previewPayslip || pdfPayload)}
        isGeneratingPdf={isGeneratingPdf}
      >
        {(previewPayslip?.all || pdfType === 'all') && employeePayslipStatement && Array.isArray(employeePayslipStatement) && employeePayslipStatement.length > 0 ? (
          <div
            id="preview-payslips-pdf-all"
            style={{
              width: '794px',
              minHeight: '1123px',
              height: 'auto',
              backgroundColor: '#ffffff',
              color: '#000000',
              fontFamily: '"Times New Roman Bold", "Times New Roman", "Times", serif',
              fontWeight: 'bold',
              padding: '40px',
              boxSizing: 'border-box',
              direction: isRTL ? 'rtl' : 'ltr',
              transform: 'scale(1.0)',
              transformOrigin: 'top center'
            }}
          >
            {employeePayslipStatement.map((payslip, index) => (
              <div key={payslip.id || index} style={{ marginBottom: index < employeePayslipStatement.length - 1 ? '40px' : 0 }}>
                <div style={{
                  border: '5px solid #000',
                  padding: '3px',
                  backgroundColor: '#ffffff',
                  boxSizing: 'border-box',
                  width: '100%',
                  minHeight: '1043px',
                  height: 'auto',
                  overflow: 'visible'
                }}>
                  <div style={{
                    border: '2px solid #000',
                    minHeight: '1033px',
                    height: 'auto',
                    padding: '18px 18px 10px',
                    position: 'relative',
                    display: 'flex',
                    flexDirection: 'column',
                    fontFamily: '"Times New Roman Bold", "Times New Roman", "Times", serif',
                    fontWeight: 'bold',
                    direction: isRTL ? 'rtl' : 'ltr',
                    backgroundColor: '#ffffff',
                    boxSizing: 'border-box',
                    width: '100%',
                    overflow: 'visible'
                  }}>
                    <div style={{
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      transform: 'translate(-50%, -50%)',
                      width: '100%',
                      height: '600px',
                      backgroundImage: `url(${headerLogo})`,
                      backgroundSize: '95%',
                      backgroundPosition: 'center center',
                      backgroundRepeat: 'no-repeat',
                      opacity: 0.1,
                      zIndex: 0,
                      pointerEvents: 'none'
                    }} />
                    <div style={{ position: 'relative', zIndex: 1, display: 'flex', flexDirection: 'column', height: '100%', justifyContent: 'space-between' }}>
                      <div style={{ flex: '0 0 auto' }}>
                        <img
                          src={`${publicUrl}/HeaderAce.png`}
                          alt="Header"
                          style={{
                            width: '100%',
                            height: 'auto',
                            objectFit: 'contain',
                            marginBottom: '8px',
                            borderBottom: '4px double #000',
                            paddingBottom: '8px',
                            flexShrink: 0
                          }}
                          crossOrigin="anonymous"
                        />
                        <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                          <div style={{ marginTop: '12px', textAlign: 'center', marginBottom: '20px' }}>
                            <h2 style={{ fontSize: '20px', fontWeight: '700', color: '#000080', margin: 0, direction: dir }}>{t('payslips.print.labels.employeePayslip', { defaultValue: isRTL ? 'كشف راتب موظف' : 'Employee Payslip' })}</h2>
                            <div style={{ fontSize: '14px', marginTop: '4px', direction: dir }}>{payslip.pay_period || ''}</div>
                          </div>
                          <div style={{ border: '1px solid #000', padding: '12px', marginBottom: '12px' }}>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '10px', fontSize: '13px', lineHeight: 1.6, direction: dir, textAlign }}>
                              <div><strong>{t('payslips.print.labels.name', { defaultValue: isRTL ? 'الاسم' : 'Name' })}:</strong> {(isRTL ? payslip.employee?.name_ar : payslip.employee?.name_en) || payslip.employee?.name || '—'}</div>
                              <div><strong>{t('payslips.print.labels.code', { defaultValue: isRTL ? 'الكود' : 'Code' })}:</strong> {payslip.employee?.employee_code || '—'}</div>
                              <div><strong>{t('payslips.print.labels.email', { defaultValue: isRTL ? 'البريد' : 'Email' })}:</strong> {payslip.employee?.email || '—'}</div>
                              <div><strong>{t('payslips.print.labels.phone', { defaultValue: isRTL ? 'الهاتف' : 'Phone' })}:</strong> {payslip.employee?.phone || '—'}</div>
                            </div>
                          </div>
                          <div style={{ border: '1px solid #000', padding: '12px', marginBottom: '12px' }}>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '10px', fontSize: '13px', lineHeight: 1.6, direction: dir, textAlign }}>
                              <div><strong>{t('payslips.print.labels.payPeriod', { defaultValue: isRTL ? 'الفترة' : 'Pay Period' })}:</strong> {payslip.pay_period || '—'}</div>
                              <div><strong>{t('payslips.print.labels.grossSalary', { defaultValue: isRTL ? 'الراتب الإجمالي' : 'Gross Salary' })}:</strong> {formatPrintCurrency(payslip.gross_salary)}</div>
                              <div><strong>{t('payslips.print.labels.netSalary', { defaultValue: isRTL ? 'الراتب الصافي' : 'Net Salary' })}:</strong> {formatPrintCurrency(payslip.net_salary)}</div>
                              <div><strong>{t('payslips.print.labels.status', { defaultValue: isRTL ? 'الحالة' : 'Status' })}:</strong> {payslip.status === 'generated' ? t('payslips.status.generated', { defaultValue: isRTL ? 'تم التوليد' : 'Generated' }) : payslip.status === 'posted' ? t('payslips.status.posted', { defaultValue: isRTL ? 'مرحل' : 'Posted' }) : payslip.status || '—'}</div>
                            </div>
                          </div>
                          {payslip.breakdown && (
                            <div style={{ marginBottom: '12px' }}>
                              <div style={{ fontWeight: '700', fontSize: '15px', color: '#0d2a50', marginBottom: '8px', direction: dir, textAlign }}>{t('payslips.print.labels.salaryDetails', { defaultValue: isRTL ? 'تفاصيل الراتب' : 'Salary Details' })}</div>
                              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px', direction: dir }}>
                                <thead>
                                  <tr style={{ backgroundColor: '#e3f2fd', color: '#0d2a50' }}>
                                    <th style={{ border: '1px solid #000', padding: '8px', textAlign }}>{t('payslips.print.labels.item', { defaultValue: isRTL ? 'البند' : 'Item' })}</th>
                                    <th style={{ border: '1px solid #000', padding: '8px', textAlign }}>{t('payslips.print.labels.amount', { defaultValue: isRTL ? 'المبلغ' : 'Amount' })}</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {payslip.breakdown.base_salary && (
                                    <tr>
                                      <td style={{ border: '1px solid #000', padding: '8px', textAlign }}>{t('payslips.print.labels.baseSalary', { defaultValue: isRTL ? 'الراتب الأساسي' : 'Base Salary' })}</td>
                                      <td style={{ border: '1px solid #000', padding: '8px', fontWeight: '700', textAlign }}>{formatPrintCurrency(payslip.breakdown.base_salary)}</td>
                                    </tr>
                                  )}
                                  {payslip.breakdown.allowances && Object.keys(payslip.breakdown.allowances).map((key) => (
                                    <tr key={key}>
                                      <td style={{ border: '1px solid #000', padding: '8px', textAlign }}>{t('payslips.print.labels.allowance', { defaultValue: isRTL ? 'بدل' : 'Allowance' })} {key}</td>
                                      <td style={{ border: '1px solid #000', padding: '8px', color: '#1976d2', fontWeight: '700', textAlign }}>+{formatPrintCurrency(payslip.breakdown.allowances[key])}</td>
                                    </tr>
                                  ))}
                                  {payslip.breakdown.fixed_deductions && Array.isArray(payslip.breakdown.fixed_deductions) && payslip.breakdown.fixed_deductions.length > 0 && payslip.breakdown.fixed_deductions.map((deduction, idx) => (
                                    <tr key={`deduction-${idx}`}>
                                      <td style={{ border: '1px solid #000', padding: '8px', textAlign }}>{deduction.name || t('payslips.print.labels.deduction', { defaultValue: isRTL ? 'خصم' : 'Deduction' })}</td>
                                      <td style={{ border: '1px solid #000', padding: '8px', color: '#c0392b', fontWeight: '700', textAlign }}>-{formatPrintCurrency(deduction.amount || deduction)}</td>
                                    </tr>
                                  ))}
                                  {payslip.breakdown.calculation_details?.absence_deduction && (
                                    <tr>
                                      <td style={{ border: '1px solid #000', padding: '8px', textAlign }}>{t('payslips.print.labels.absenceDeduction', { defaultValue: isRTL ? 'خصم الغياب' : 'Absence Deduction' })}</td>
                                      <td style={{ border: '1px solid #000', padding: '8px', color: '#c0392b', fontWeight: '700', textAlign }}>-{formatPrintCurrency(payslip.breakdown.calculation_details.absence_deduction)}</td>
                                    </tr>
                                  )}
                                </tbody>
                              </table>
                            </div>
                          )}
                          {payslip.breakdown?.calculation_details && (
                            <div style={{ border: '1px solid #000', padding: '12px', fontSize: '12px', lineHeight: 1.7, direction: dir, textAlign }}>
                              <div style={{ fontWeight: '700', marginBottom: '8px', color: '#0d2a50' }}>{t('payslips.print.labels.calculationDetails', { defaultValue: isRTL ? 'تفاصيل الحساب' : 'Calculation Details' })}</div>
                              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '10px', direction: dir }}>
                                {payslip.breakdown.calculation_details.total_work_days !== undefined && (
                                  <div style={{ direction: dir, textAlign }}><strong>{t('payslips.print.labels.totalWorkDays', { defaultValue: isRTL ? 'أيام العمل' : 'Total Work Days' })}:</strong> {payslip.breakdown.calculation_details.total_work_days}</div>
                                )}
                                {payslip.breakdown.calculation_details.attended_days !== undefined && (
                                  <div style={{ direction: dir, textAlign }}><strong>{t('payslips.print.labels.attendedDays', { defaultValue: isRTL ? 'أيام الحضور' : 'Attended Days' })}:</strong> {payslip.breakdown.calculation_details.attended_days}</div>
                                )}
                                {payslip.breakdown.calculation_details.leave_days !== undefined && (
                                  <div style={{ direction: dir, textAlign }}><strong>{t('payslips.print.labels.leaveDays', { defaultValue: isRTL ? 'أيام الإجازة' : 'Leave Days' })}:</strong> {payslip.breakdown.calculation_details.leave_days}</div>
                                )}
                                {payslip.breakdown.calculation_details.absent_days !== undefined && (
                                  <div style={{ direction: dir, textAlign }}><strong>{t('payslips.print.labels.absentDays', { defaultValue: isRTL ? 'أيام الغياب' : 'Absent Days' })}:</strong> {payslip.breakdown.calculation_details.absent_days}</div>
                                )}
                                {payslip.breakdown.calculation_details.daily_rate !== undefined && (
                                  <div style={{ direction: dir, textAlign }}><strong>{t('payslips.print.labels.dailyRate', { defaultValue: isRTL ? 'المعدل اليومي' : 'Daily Rate' })}:</strong> {formatPrintCurrency(payslip.breakdown.calculation_details.daily_rate)}</div>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                      <div style={{ flexShrink: 0, paddingTop: '20px', marginTop: 'auto' }}>
                        <img
                          src={`${publicUrl}/FooterAce.png`}
                          alt="Footer"
                          style={{
                            width: '100%',
                            height: 'auto',
                            objectFit: 'contain',
                            borderTop: '4px double #000',
                            paddingTop: '8px'
                          }}
                          crossOrigin="anonymous"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (previewPayslip || pdfPayload) && (
          <div
            id={`preview-payslip-pdf-${(previewPayslip || pdfPayload)?.id}`}
            style={{
              width: '794px',
              minHeight: '1123px',
              height: 'auto',
              backgroundColor: '#ffffff',
              color: '#000000',
              fontFamily: '"Times New Roman Bold", "Times New Roman", "Times", serif',
              fontWeight: 'bold',
              padding: '40px',
              boxSizing: 'border-box',
              direction: isRTL ? 'rtl' : 'ltr',
              transform: 'scale(1.0)',
              transformOrigin: 'top center'
            }}
          >
            <div style={{
              border: '5px solid #000',
              padding: '3px',
              backgroundColor: '#ffffff',
              boxSizing: 'border-box',
              width: '100%',
              minHeight: '1043px',
              height: 'auto',
              overflow: 'visible'
            }}>
              <div style={{
                border: '2px solid #000',
                minHeight: '1033px',
                height: 'auto',
                padding: '18px 18px 10px',
                position: 'relative',
                display: 'flex',
                flexDirection: 'column',
                fontFamily: '"Times New Roman Bold", "Times New Roman", "Times", serif',
                fontWeight: 'bold',
                direction: isRTL ? 'rtl' : 'ltr',
                backgroundColor: '#ffffff',
                boxSizing: 'border-box',
                width: '100%',
                overflow: 'visible'
              }}>
                <div style={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  width: '100%',
                  height: '600px',
                  backgroundImage: `url(${headerLogo})`,
                  backgroundSize: '95%',
                  backgroundPosition: 'center center',
                  backgroundRepeat: 'no-repeat',
                  opacity: 0.1,
                  zIndex: 0,
                  pointerEvents: 'none'
                }} />
                <div style={{ position: 'relative', zIndex: 1, display: 'flex', flexDirection: 'column', height: '100%', justifyContent: 'space-between' }}>
                  <div style={{ flex: '0 0 auto' }}>
                    <img
                      src={`${publicUrl}/HeaderAce.png`}
                      alt="Header"
                      style={{
                        width: '100%',
                        height: 'auto',
                        objectFit: 'contain',
                        marginBottom: '8px',
                        borderBottom: '4px double #000',
                        paddingBottom: '8px',
                        flexShrink: 0
                      }}
                      crossOrigin="anonymous"
                    />
                    <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                      {(() => {
                        const payslip = previewPayslip || pdfPayload;
                        return payslip ? (
                          <>
                            <div style={{ marginTop: '12px', textAlign: 'center', marginBottom: '20px' }}>
                              <h2 style={{ fontSize: '20px', fontWeight: '700', color: '#000080', margin: 0, direction: dir }}>{t('payslips.print.labels.employeePayslip', { defaultValue: isRTL ? 'كشف راتب موظف' : 'Employee Payslip' })}</h2>
                              <div style={{ fontSize: '14px', marginTop: '4px', direction: dir }}>{payslip.pay_period || ''}</div>
                            </div>
                            <div style={{ border: '1px solid #000', padding: '12px', marginBottom: '12px' }}>
                              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '10px', fontSize: '13px', lineHeight: 1.6, direction: dir, textAlign }}>
                                <div><strong>{t('payslips.print.labels.name', { defaultValue: isRTL ? 'الاسم' : 'Name' })}:</strong> {(isRTL ? payslip.employee?.name_ar : payslip.employee?.name_en) || payslip.employee?.name || '—'}</div>
                                <div><strong>{t('payslips.print.labels.code', { defaultValue: isRTL ? 'الكود' : 'Code' })}:</strong> {payslip.employee?.employee_code || '—'}</div>
                                <div><strong>{t('payslips.print.labels.email', { defaultValue: isRTL ? 'البريد' : 'Email' })}:</strong> {payslip.employee?.email || '—'}</div>
                                <div><strong>{t('payslips.print.labels.phone', { defaultValue: isRTL ? 'الهاتف' : 'Phone' })}:</strong> {payslip.employee?.phone || '—'}</div>
                              </div>
                            </div>
                            <div style={{ border: '1px solid #000', padding: '12px', marginBottom: '12px' }}>
                              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '10px', fontSize: '13px', lineHeight: 1.6, direction: dir, textAlign }}>
                                <div><strong>{t('payslips.print.labels.payPeriod', { defaultValue: isRTL ? 'الفترة' : 'Pay Period' })}:</strong> {payslip.pay_period || '—'}</div>
                                <div><strong>{t('payslips.print.labels.grossSalary', { defaultValue: isRTL ? 'الراتب الإجمالي' : 'Gross Salary' })}:</strong> {formatPrintCurrency(payslip.gross_salary)}</div>
                                <div><strong>{t('payslips.print.labels.netSalary', { defaultValue: isRTL ? 'الراتب الصافي' : 'Net Salary' })}:</strong> {formatPrintCurrency(payslip.net_salary)}</div>
                                <div><strong>{t('payslips.print.labels.status', { defaultValue: isRTL ? 'الحالة' : 'Status' })}:</strong> {payslip.status === 'generated' ? t('payslips.status.generated', { defaultValue: isRTL ? 'تم التوليد' : 'Generated' }) : payslip.status === 'posted' ? t('payslips.status.posted', { defaultValue: isRTL ? 'مرحل' : 'Posted' }) : payslip.status || '—'}</div>
                              </div>
                            </div>
                            {payslip.breakdown && (
                              <div style={{ marginBottom: '12px' }}>
                                <div style={{ fontWeight: '700', fontSize: '15px', color: '#0d2a50', marginBottom: '8px', direction: dir, textAlign }}>{t('payslips.print.labels.salaryDetails', { defaultValue: isRTL ? 'تفاصيل الراتب' : 'Salary Details' })}</div>
                                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px', direction: dir }}>
                                  <thead>
                                    <tr style={{ backgroundColor: '#e3f2fd', color: '#0d2a50' }}>
                                      <th style={{ border: '1px solid #000', padding: '8px', textAlign }}>{t('payslips.print.labels.item', { defaultValue: isRTL ? 'البند' : 'Item' })}</th>
                                      <th style={{ border: '1px solid #000', padding: '8px', textAlign }}>{t('payslips.print.labels.amount', { defaultValue: isRTL ? 'المبلغ' : 'Amount' })}</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {payslip.breakdown.base_salary && (
                                      <tr>
                                        <td style={{ border: '1px solid #000', padding: '8px', textAlign }}>{t('payslips.print.labels.baseSalary', { defaultValue: isRTL ? 'الراتب الأساسي' : 'Base Salary' })}</td>
                                        <td style={{ border: '1px solid #000', padding: '8px', fontWeight: '700', textAlign }}>{formatPrintCurrency(payslip.breakdown.base_salary)}</td>
                                      </tr>
                                    )}
                                    {payslip.breakdown.allowances && Object.keys(payslip.breakdown.allowances).map((key) => (
                                      <tr key={key}>
                                        <td style={{ border: '1px solid #000', padding: '8px', textAlign }}>{t('payslips.print.labels.allowance', { defaultValue: isRTL ? 'بدل' : 'Allowance' })} {key}</td>
                                        <td style={{ border: '1px solid #000', padding: '8px', color: '#1976d2', fontWeight: '700', textAlign }}>+{formatPrintCurrency(payslip.breakdown.allowances[key])}</td>
                                      </tr>
                                    ))}
                                    {payslip.breakdown.fixed_deductions && Array.isArray(payslip.breakdown.fixed_deductions) && payslip.breakdown.fixed_deductions.length > 0 && payslip.breakdown.fixed_deductions.map((deduction, idx) => (
                                      <tr key={`deduction-${idx}`}>
                                        <td style={{ border: '1px solid #000', padding: '8px', textAlign }}>{deduction.name || t('payslips.print.labels.deduction', { defaultValue: isRTL ? 'خصم' : 'Deduction' })}</td>
                                        <td style={{ border: '1px solid #000', padding: '8px', color: '#c0392b', fontWeight: '700', textAlign }}>-{formatPrintCurrency(deduction.amount || deduction)}</td>
                                      </tr>
                                    ))}
                                    {payslip.breakdown.calculation_details?.absence_deduction && (
                                      <tr>
                                        <td style={{ border: '1px solid #000', padding: '8px', textAlign }}>{t('payslips.print.labels.absenceDeduction', { defaultValue: isRTL ? 'خصم الغياب' : 'Absence Deduction' })}</td>
                                        <td style={{ border: '1px solid #000', padding: '8px', color: '#c0392b', fontWeight: '700', textAlign }}>-{formatPrintCurrency(payslip.breakdown.calculation_details.absence_deduction)}</td>
                                      </tr>
                                    )}
                                  </tbody>
                                </table>
                              </div>
                            )}
                            {payslip.breakdown?.calculation_details && (
                              <div style={{ border: '1px solid #000', padding: '12px', fontSize: '12px', lineHeight: 1.7, direction: dir, textAlign }}>
                                <div style={{ fontWeight: '700', marginBottom: '8px', color: '#0d2a50' }}>{t('payslips.print.labels.calculationDetails', { defaultValue: isRTL ? 'تفاصيل الحساب' : 'Calculation Details' })}</div>
                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '10px', direction: dir }}>
                                  {payslip.breakdown.calculation_details.total_work_days !== undefined && (
                                    <div style={{ direction: dir, textAlign }}><strong>{t('payslips.print.labels.totalWorkDays', { defaultValue: isRTL ? 'أيام العمل' : 'Total Work Days' })}:</strong> {payslip.breakdown.calculation_details.total_work_days}</div>
                                  )}
                                  {payslip.breakdown.calculation_details.attended_days !== undefined && (
                                    <div style={{ direction: dir, textAlign }}><strong>{t('payslips.print.labels.attendedDays', { defaultValue: isRTL ? 'أيام الحضور' : 'Attended Days' })}:</strong> {payslip.breakdown.calculation_details.attended_days}</div>
                                  )}
                                  {payslip.breakdown.calculation_details.leave_days !== undefined && (
                                    <div style={{ direction: dir, textAlign }}><strong>{t('payslips.print.labels.leaveDays', { defaultValue: isRTL ? 'أيام الإجازة' : 'Leave Days' })}:</strong> {payslip.breakdown.calculation_details.leave_days}</div>
                                  )}
                                  {payslip.breakdown.calculation_details.absent_days !== undefined && (
                                    <div style={{ direction: dir, textAlign }}><strong>{t('payslips.print.labels.absentDays', { defaultValue: isRTL ? 'أيام الغياب' : 'Absent Days' })}:</strong> {payslip.breakdown.calculation_details.absent_days}</div>
                                  )}
                                  {payslip.breakdown.calculation_details.daily_rate !== undefined && (
                                    <div style={{ direction: dir, textAlign }}><strong>{t('payslips.print.labels.dailyRate', { defaultValue: isRTL ? 'المعدل اليومي' : 'Daily Rate' })}:</strong> {formatPrintCurrency(payslip.breakdown.calculation_details.daily_rate)}</div>
                                  )}
                                </div>
                              </div>
                            )}
                          </>
                        ) : null;
                      })()}
                    </div>
                  </div>
                  <div style={{ flexShrink: 0, paddingTop: '20px', marginTop: 'auto' }}>
                    <img
                      src={`${publicUrl}/FooterAce.png`}
                      alt="Footer"
                      style={{
                        width: '100%',
                        height: 'auto',
                        objectFit: 'contain',
                        borderTop: '4px double #000',
                        paddingTop: '8px'
                      }}
                      crossOrigin="anonymous"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </PdfPreviewModal>
    </div>
  );
};

export default PayslipsStatement;

